import { useState, useCallback } from 'react';
import clsx from 'clsx';
import { Form } from 'react-final-form';
import Page from '../layout/components/Page';
import { useSubmitLoan } from '../hooks/loan/useSubmitLoan';
import RentLoanForm from '../components/Loan/Apply/RentLoanForm';
import LoanTypeComponent from '../components/Loan/Apply/LoanType';
import LoadingIndicator from '../components/Common/LoadingIndicator';
import EquityLoanForm from '../components/Loan/Apply/EquityLoanForm';
import PaydayLoanForm from '../components/Loan/Apply/PaydayLoanForm';
import MortgageLoanForm from '../components/Loan/Apply/MortgageLoanForm';
import { LoanFormValues, LoanType } from '../interfaces';
import ProfileInformationComponent from '../components/Loan/Apply/ProfileInformation';
import { useFetchBorrowerProfile } from '../hooks/complete_profile/useFetchBorrowerProfile';

const LoansApplyPage = () => {
  const [step, setStep] = useState(0);
  const [loanType, setLoanType] = useState('');
  const { submitting, submitLoanHandler } = useSubmitLoan();
  const { profile } = useFetchBorrowerProfile();

  const wizardHeaders = ['Loan Type', 'Loan Information', 'Profile Information'];
  const activeHeader = (index: number) => (step === index ? 'current' : null);

  const renderLoanForm = useCallback(() => {
    switch (loanType) {
      case LoanType.MORTGAGE:
        return <MortgageLoanForm />;
      case LoanType.RENT:
        return <RentLoanForm />;
      case LoanType.EQUITY_FINANCE:
        return <EquityLoanForm />;
      case LoanType.PAYDAY:
        return <PaydayLoanForm />;
      default:
        return null;
    }
  }, [loanType]);

  const renderActiveStep = useCallback(() => {
    switch (step) {
      case 0:
        return <LoanTypeComponent />;
      case 1:
        return renderLoanForm();
      case 2:
        return <ProfileInformationComponent profile={profile} />;
      default:
        return null;
    }
  }, [step, renderLoanForm, profile]);

  const isSubmitButton = step === wizardHeaders.length - 1 ? true : false;
  const previous = () => setStep(step - 1);

  const onSubmitHandler = (payload: LoanFormValues) => {
    const { loan_type } = payload;

    if (step === 0) {
      setLoanType(loan_type);
    }

    if (step !== wizardHeaders.length - 1) {
      setStep(step + 1);
    }

    if (step === wizardHeaders.length - 1) {
      submitLoanHandler(payload);
    }
  };

  return (
    <Page title='Apply For Loan' icon='bi-briefcase'>
      <div className='card'>
        <div className='card-body'>
          <div className='stepper stepper-links d-flex flex-column'>
            <div className='stepper-nav mb-5'>
              {wizardHeaders.map((header: string, index: number) => (
                <div key={header} className={clsx('stepper-item', activeHeader(index))} data-kt-stepper-element='nav'>
                  <h3 className='stepper-title'>{header}</h3>
                </div>
              ))}
            </div>

            <Form
              initialValues={{
                firstname: profile?.user.firstname,
                lastname: profile?.user.lastname,
                address: profile?.address,
                city: profile?.address_city,
                state: profile?.address_state.name,
                country: profile?.address_country.name,
                occupation: profile?.occupation,
                employment_type: profile?.is_self_employed,
                employer_name: profile?.employer_name,
                employer_city: profile?.employer_city,
                employer_country: profile?.employer_country?.name,
                employer_state: profile?.employer_state?.name,
                job_title: profile?.job_title,
                employment_duration: profile?.duration,
                business_name: profile?.business_name,
                business_city: profile?.business_city,
                business_state: profile?.business_state?.name,
                business_country: profile?.business_country?.name,
                rc_no: profile?.rc_no,
                business_type: profile?.business_type,
                business_duration: profile?.duration,
                linkedin: profile?.linkedin,
                bvn: profile?.bvn,
                nin: profile?.nin,
              }}
              onSubmit={onSubmitHandler}
              render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <div className='mx-auto mw-600px w-100 pt-10 pb-10'>{renderActiveStep()}</div>

                  <div className='d-flex flex-stack pt-15'>
                    <div className='mr-2'>
                      {step !== 0 && (
                        <button type='button' onClick={previous} className='btn btn-lg btn-light-primary me-3'>
                          Back
                        </button>
                      )}
                    </div>

                    <div>
                      <button type='submit' className='btn btn-lg btn-primary me-3'>
                        <span className='indicator-label'>
                          {!isSubmitButton && 'Continue'}
                          {isSubmitButton && (submitting ? <LoadingIndicator /> : 'Submit')}
                        </span>
                      </button>
                    </div>
                  </div>
                </form>
              )}
            />
          </div>
        </div>
      </div>
    </Page>
  );
};

export default LoansApplyPage;
